import isEqual from "lodash/isEqual";
import { createDonor, lastDonorId, updateDonor } from "../../../redux/donorSlice";
import {
  createDonation,
  transactionId,
  updateDonation,
} from "../../../redux/donationSlice";
import { logger } from "../../../utils/logger";

/**
 * Handle save donor (create or update)
 */
export const handleSaveDonor = async (
  data,
  editData,
  isExistingDonorOpen,
  dispatch,
  user,
  setFeedback,
  setEditData,
  setIsDonorFormOpen,
  setIsDonationFormOpen,
  setOpenConfirm
) => {
  try {
    if (editData) {
      // Update existing donor
      if (isEqual(editData, data)) {
        setFeedback({
          open: true,
          message: "No changes detected",
          severity: "info",
        });
        logger.warn("No changes made in Donor form", {
          username: user?.username,
          role: user?.role,
        });
        return;
      }

      const response = await dispatch(updateDonor(data)).unwrap();
      const updatedDonor = response;

      if (!updatedDonor.error) {
        setFeedback({
          open: true,
          message: "Donor updated successfully!",
          severity: "success",
        });
        logger.info("Donor updated successfully", {
          donorId: data.donorId,
          editedFields: Object.keys(data).filter((k) => data[k] !== editData[k]),
          username: user?.username,
          role: user?.role,
        });
        setIsDonorFormOpen(false);
        setIsDonationFormOpen(false);
      }
    } else {
      // Create new donor
      const donorIdResponse = await dispatch(lastDonorId(data));
      const newDonorId = !isNaN(donorIdResponse.payload)
        ? donorIdResponse.payload
        : 1;
      const copyData = { ...data, donorId: newDonorId };
      const response = await dispatch(createDonor(copyData)).unwrap();
      const createdDonor = response.donor || response;

      setEditData(copyData);
      setFeedback({
        open: true,
        message: "New donor added successfully!",
        severity: "success",
      });
      logger.info("New donor created", {
        donorId: newDonorId,
        username: user?.username,
        role: user?.role,
      });
      setIsDonorFormOpen(false);
      setIsDonationFormOpen(true);
    }

    // Show confirmation dialog if editing existing donor
    if (isExistingDonorOpen && editData) {
      setOpenConfirm(true);
    }
  } catch (error) {
    setFeedback({
      open: true,
      message: "An error occurred. Please try again",
      severity: "error",
    });
    logger.error("Some technical error occur during donor form submission", {
      data,
      error: error,
      username: user?.username,
      role: user?.role,
    });
  }
};

/**
 * Handle save donation (create or update)
 */
export const handleSaveDonation = async (
  data,
  editData,
  dispatch,
  user,
  setFeedback,
  setIsDonorFormOpen,
  setIsDonationFormOpen
) => {
  try {
    if (editData && editData.transactionId) {
      // Update existing donation
      if (isEqual(editData, data)) {
        setFeedback({
          open: true,
          message: "No changes detected",
          severity: "info",
        });
        logger.warn("No changes made in donation form", {
          donorId: editData?.donorId,
          username: user?.username,
          role: user?.role,
        });
        return;
      }

      await dispatch(updateDonation(data));
      setFeedback({
        open: true,
        message: "Donation updated successfully!",
        severity: "success",
      });
      logger.info("Donation updated", {
        transactionId: data?.transactionId,
        donorId: data?.donorId,
        username: user?.username,
        role: user?.role,
      });
      setIsDonorFormOpen(false);
      setIsDonationFormOpen(false);
    } else {
      // Create new donation
      const transactionResponse = await dispatch(transactionId(data));
      const newtransactionId = transactionResponse.payload;
      const copyData = { ...data, transactionId: newtransactionId };
      const response = await dispatch(createDonation(copyData));
      const newDonation = response.payload;

      if (!newDonation.error) {
        setFeedback({
          open: true,
          message: "New donation added successfully!",
          severity: "success",
        });
        logger.info("New donation created", {
          transactionId: newtransactionId,
          donorId: data?.donorId,
          username: user?.username,
          role: user?.role,
        });
        setIsDonorFormOpen(false);
        setIsDonationFormOpen(false);
      } else {
        setFeedback({
          open: true,
          message: "Failed to add donation. Please try again.",
          severity: "error",
        });
        logger.error("Error saving donation", {
          data,
          error: newDonation?.error,
          username: user?.username,
          role: user?.role,
        });
      }
    }
  } catch (error) {
    setFeedback({
      open: true,
      message: "An error occurred. Please try again.",
      severity: "error",
    });
    logger.error("Some Technical error occur during donation form submit", {
      data,
      error: error,
      username: user?.username,
      role: user?.role,
    });
  }
};

/**
 * Open donor form for viewing/editing existing donor
 */
export const openDonorForm = (donor, user, setEditData, setIsExistingDonorOpen) => {
  logger.info("Donor Form modal opened for Edit data", {
    username: user?.username,
    role: user?.role,
  });
  setEditData(donor);
  setIsExistingDonorOpen(true);
};

/**
 * Handle add new donor button click
 */
export const handleAddDonor = (user, setEditData, setIsDonorFormOpen) => {
  setEditData(null);
  setIsDonorFormOpen(true);
  logger.info("Donor Form opened for adding new donor", {
    username: user?.username,
    role: user?.role,
  });
};

/**
 * Handle edit donor button click
 */
export const handleEditDonor = (
  donor,
  user,
  setEditData,
  setIsDonorFormOpen,
  setIsDonationFormOpen
) => {
  setEditData(donor);
  setIsDonorFormOpen(true);
  setIsDonationFormOpen(false);
  logger.info("Donor Form opened for editing existing donor", {
    donorId: donor.donorId,
    username: user?.username,
    role: user?.role,
  });
};

/**
 * Handle add donation from donor modal
 */
export const handleAddDonation = (
  data,
  user,
  setIsDonorFormOpen,
  setIsExistingDonorOpen,
  setIsDonationFormOpen
) => {
  setIsDonorFormOpen(false);
  setIsExistingDonorOpen(false);
  setIsDonationFormOpen(true);
  logger.info("Donation form opened", {
    data: data?.donorId,
    username: user?.username,
    role: user?.role,
  });
};

/**
 * Handle modal submit (trigger form submission)
 */
export const handleModalSubmit = (formRef) => {
  if (formRef.current) {
    formRef.current.submitForm();
  }
};

/**
 * Handle modal close
 */
export const handleCloseModal = (setIsExistingDonorOpen) => {
  setIsExistingDonorOpen(false);
};

/**
 * Handle feedback close
 */
export const handleCloseFeedback = (feedback, setFeedback) => {
  setFeedback({ ...feedback, open: false });
};
